'use client'
import React, { useState, useEffect } from 'react'
import { Media } from '@/payload-types'
import Image from 'next/image'
import Loader from '@/components/Loader/Component'

export const PhotoGallery: React.FC<{ photos: Media[], imagesPerPage: number, id: string }> = ({ photos, imagesPerPage, id }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0)
  const [currentThumbnailPage, setCurrentThumbnailPage] = useState(1)
  const [showCopiedMessage, setShowCopiedMessage] = useState(false)
  const [isImageLoading, setIsImageLoading] = useState(false)

  const thumbnailsPerPage = imagesPerPage
  const totalThumbnailPages = Math.ceil(photos.length / thumbnailsPerPage)
  const startIndex = (currentThumbnailPage - 1) * thumbnailsPerPage
  const endIndex = startIndex + thumbnailsPerPage
  const currentThumbnails = photos.slice(startIndex, endIndex)

  const selectImage = (index: number) => {
    setIsImageLoading(true)
    setSelectedImageIndex(index)
  }

  const goToPrevious = () => {
    const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : photos.length - 1
    setIsImageLoading(true)
    setSelectedImageIndex(newIndex)

    // Check if the new index is outside the current thumbnail page range
    const newThumbnailPage = Math.ceil((newIndex + 1) / thumbnailsPerPage)
    if (newThumbnailPage !== currentThumbnailPage) {
      setCurrentThumbnailPage(newThumbnailPage)
    }
  }

  const goToNext = () => {
    const newIndex = selectedImageIndex < photos.length - 1 ? selectedImageIndex + 1 : 0
    setIsImageLoading(true)
    setSelectedImageIndex(newIndex)

    // Check if the new index is outside the current thumbnail page range
    const newThumbnailPage = Math.ceil((newIndex + 1) / thumbnailsPerPage)
    if (newThumbnailPage !== currentThumbnailPage) {
      setCurrentThumbnailPage(newThumbnailPage)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goToPrevious()
    if (e.key === 'ArrowRight') goToNext()
  }

  const sharePhoto = async (photoId: string) => {
    try {
      const url = new URL(window.location.href)
      url.searchParams.set('photo', photoId)
      await navigator.clipboard.writeText(url.toString())

      setShowCopiedMessage(true)
      setTimeout(() => {
        setShowCopiedMessage(false)
      }, 3000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      // Fallback: show URL in alert
      const url = new URL(window.location.href)
      url.searchParams.set('photo', photoId)
      alert(`Kopieer deze link om te delen: ${url.toString()}`)
    }
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const photoId = urlParams.get('photo')
    if (photoId) {
      const photoIndex = photos.findIndex(photo => photo.id === photoId)
      if (photoIndex !== -1) {
        setSelectedImageIndex(photoIndex)
        const thumbnailPage = Math.ceil((photoIndex + 1) / thumbnailsPerPage)
        setCurrentThumbnailPage(thumbnailPage)
      }
    }
  }, [photos, thumbnailsPerPage])

  return (
    <div className="w-full" onKeyDown={handleKeyDown} tabIndex={0}>
      {photos.length > 0 && (
        <>
          <div className="relative mb-6">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg shadow-lg bg-black">
              <Image
                src={photos[selectedImageIndex].sizes?.large?.url || photos[selectedImageIndex].url || ''}
                alt={photos[selectedImageIndex].alt || ''}
                fill
                className="object-contain"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw"
                priority
                onLoad={() => setIsImageLoading(false)}
              />
              {isImageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black">
                  <Loader />
                </div>
              )}
            </div>

            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full p-2 transition-all"
              aria-label="Previous image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full p-2 transition-all"
              aria-label="Next image"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm font-medium">
              {selectedImageIndex + 1} / {photos.length}
            </div>

            <button
              onClick={() => {
                const link = document.createElement('a')
                link.href = photos[selectedImageIndex].sizes?.large?.url || photos[selectedImageIndex].url || ''
                link.download = photos[selectedImageIndex].filename || 'photo.jpg'
                link.click()
              }}
              className="absolute bottom-4 right-16 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full p-2 transition-all"
              aria-label="Download photo"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>

            <button
              onClick={() => sharePhoto(photos[selectedImageIndex].id)}
              className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white hover:bg-opacity-70 rounded-full p-2 transition-all"
              aria-label="Share photo"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
            </button>

            {showCopiedMessage && (
              <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-2 rounded-md text-sm font-medium shadow-lg">
                Link gekopieerd! 📋
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-center gap-2 p-2">
              {currentThumbnails.map((photo, index) => {
                const globalIndex = startIndex + index
                return (
                  <div
                    key={photo.id}
                    className={`relative w-16 h-16 cursor-pointer overflow-hidden rounded-md transition-all ${
                      globalIndex === selectedImageIndex
                        ? 'ring-2 ring-blue-500 shadow-lg'
                        : 'hover:shadow-md hover:scale-105'
                    }`}
                    onClick={() => selectImage(globalIndex)}
                  >
                    <Image
                      src={photo.sizes?.small?.url || photo.sizes?.thumbnail?.url || photo.url || ''}
                      alt={photo.alt || ''}
                      fill
                      className="object-cover"
                      sizes="(max-width: 640px) 25vw, (max-width: 768px) 16vw, (max-width: 1024px) 12vw, 10vw"
                    />
                  </div>
                )
              })}
          </div>

          {totalThumbnailPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-4">
              <button
                onClick={() => setCurrentThumbnailPage(currentThumbnailPage - 1)}
                disabled={currentThumbnailPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <span className="text-sm text-gray-600">
                {currentThumbnailPage} / {totalThumbnailPages}
              </span>

              <button
                onClick={() => setCurrentThumbnailPage(currentThumbnailPage + 1)}
                disabled={currentThumbnailPage === totalThumbnailPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
